// @ts-nocheck
import { useQcClassification } from "@/components/quality/qc-form/useQcClassification";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm, useStore } from "@tanstack/react-form";
import { CheckCircle2, ClipboardCheck, Layers, Loader2, PenLine, Play, Save } from "lucide-react";
import { normalizeReportStage, type QCProcessFormValues } from "@/lib/schemas/qc";
import { useAuthStore } from "@/stores/auth.store";
import { trpcClient } from "@/lib/trpc";
import { getProductionOrderByIdQuery } from "@/lib/trpc-helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { computeQcVerdict, summarizeQcDefects } from "@/lib/aql";
import { formatVnDateTime, ipqcSetupResultFrom4M, rollupIpqcLot } from "@apv-erp/status";
import {
  CriteriaTable,
  LegacyMeasurements,
  QcInfoField,
  QcInfoGrid,
  QcInfoValue,
  QcTemplateSelect,
  SamplingRow,
  VerdictBar,
  normalizeChecklistItems,
  resolveFormStatus,
  useQcSamplingState,
  useQcTemplateCriteria,
  type IsoCriterionResult,
} from "@/components/quality/qc-form";
import { isIpqcNewFlow, legacyParamRows, repeatColumnsOf, splitIpqcItems } from "@/components/quality/qc-form/ipqc";
import {
  IpqcRoundsTable,
  IpqcStageBar,
  SAMPLE_FREQUENCY_OPTIONS,
  frequencyLabel,
  normalizePatrolLogs,
  type IpqcPatrolLogEntry,
} from "@/components/quality/IpqcThreeStageSection";
import { normalizeDefectTallyItems, StageDefectSelectorTable, type DefectTallyItem } from "@/components/quality/StageDefectSelectorTable";
import { QcCategorizedMediaSection } from "@/components/quality/QcCategorizedMediaSection";

export interface QCProcessFormPrefill {
  productionOrderId?: string;
  routingStepId?: string;
  machineId?: string;
  processStep?: string;
  processStage?: string;
  /** Giữ cho liên kết cũ; giai đoạn mới luôn bắt đầu ở Duyệt setup */
  checkType?: "FIRST_PIECE" | "ROUTINE" | "PATROL";
}

export interface QCProcessFormProps {
  initialData?: any;
  onSubmit: (data: QCProcessFormValues) => Promise<void> | void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  isDrawerMode?: boolean;
  prefill?: QCProcessFormPrefill;
}

export const OFFSET_PROCESS_STAGES = [
  { code: "CUTTING", label: "1. Cắt giấy", defaultStep: "Xả cuộn & Cắt giấy" },
  { code: "PRINTING", label: "2. In Offset", defaultStep: "In Offset" },
  { code: "LAMINATING", label: "3. Cán màng", defaultStep: "Cán màng" },
  { code: "COATING", label: "4. Tráng phủ UV", defaultStep: "Tráng phủ UV/Varnish" },
  { code: "DIE_CUTTING", label: "5. Bế / Ép kim", defaultStep: "Bế hộp" },
  { code: "GLUING", label: "6. Dán hộp", defaultStep: "Dán hộp" },
];

export function inferProcessStageClient(stepName?: string | null, explicitStage?: string | null): string {
  // Mã công đoạn đã chọn rõ (kể cả công đoạn người dùng tự thêm trong Cấu hình > Chất lượng) được giữ nguyên
  if (explicitStage && String(explicitStage).trim()) {
    return explicitStage.toUpperCase();
  }
  const s = (stepName || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
  if (s.includes("cat") || s.includes("xa giay") || s.includes("cutting")) return "CUTTING";
  if (s.includes("can mang") || s.includes("laminat")) return "LAMINATING";
  if (s.includes("trang phu") || s.includes("uv") || s.includes("varnish") || s.includes("opv") || s.includes("coating")) return "COATING";
  if (s.includes("be") || s.includes("ep kim") || s.includes("dap noi") || s.includes("boi") || s.includes("die")) return "DIE_CUTTING";
  if (s.includes("dan") || s.includes("gap") || s.includes("glu")) return "GLUING";
  return "PRINTING";
}

const SETUP_RESULT_LABEL: Record<string, string> = { PASSED: "Đạt", ADJUST_REQUIRED: "Chỉnh máy", FAILED: "Không đạt" };

function Section({
  title,
  testId,
  right,
  children,
  tone = "slate",
}: {
  title: React.ReactNode;
  testId?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
  tone?: "slate" | "blue" | "violet" | "amber" | "emerald";
}) {
  const head: Record<string, string> = {
    slate: "bg-slate-50 border-slate-200",
    blue: "bg-blue-50/70 border-blue-200",
    violet: "bg-violet-50/70 border-violet-200",
    amber: "bg-amber-50/70 border-amber-200",
    emerald: "bg-emerald-50/70 border-emerald-200",
  };
  return (
    <section className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xs" data-testid={testId}>
      <div className={cn("flex flex-wrap items-center justify-between gap-2 border-b px-3.5 py-2", head[tone])}>
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">{title}</h3>
        {right ? <div className="flex min-w-0 flex-wrap items-center gap-2">{right}</div> : null}
      </div>
      <div className="space-y-3 p-3">{children}</div>
    </section>
  );
}

type Action = "save" | "signoff" | "release" | "close";

export function QCProcessForm({ initialData, onSubmit, onCancel, isSubmitting = false, prefill }: QCProcessFormProps) {
  const isEditMode = !!initialData?.id;
  /** Phiếu lập trước đợt 3 (không có checklist 4M): giữ luồng cũ, không ký mẫu / cho sản xuất */
  const isLegacy = isEditMode && !isIpqcNewFlow(initialData);
  const currentUser = useAuthStore((s) => s.user);

  const signed = Boolean(initialData?.sampleApprovedAt);
  const released = Boolean(initialData?.releasedAt);

  const defaultProcessStep = initialData?.processStep || prefill?.processStep || "In Offset";
  const defaultProcessStage = inferProcessStageClient(defaultProcessStep, initialData?.processStage || prefill?.processStage);

  const [fourMItems, setFourMItems] = useState<IsoCriterionResult[]>(() =>
    isLegacy ? [] : Array.isArray(initialData?.fourMChecklist) ? initialData.fourMChecklist : [],
  );
  const [checklistItems, setChecklistItems] = useState<IsoCriterionResult[]>(() => {
    if (!initialData) return [];
    return normalizeChecklistItems(initialData?.template?.schema?.fields || initialData?.template?.criteria || [], initialData?.checklist).filter(
      (it) => isLegacy || !fourMItems.some((f) => f.id === it.id),
    );
  });
  const [rounds, setRounds] = useState<IpqcPatrolLogEntry[]>(() => normalizePatrolLogs(initialData?.patrolLogs));
  const [defectDetails, setDefectDetails] = useState<DefectTallyItem[]>(() => normalizeDefectTallyItems(initialData?.defectDetails));
  const [attachments, setAttachments] = useState<any>(() => initialData?.attachments || []);
  const [setupFailed, setSetupFailed] = useState<boolean>(initialData?.setupResult === "FAILED");
  const [legacySetupResult, setLegacySetupResult] = useState<string>(initialData?.setupResult || "PASSED");
  const [override, setOverride] = useState<boolean>(String(initialData?.status || "").toUpperCase() === "WARNING");
  const [overrideReason, setOverrideReason] = useState<string>(initialData?.overrideReason || "");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const actionRef = useRef<Action>("save");

  const sampling = useQcSamplingState(
    {
      lotSize: Math.max(0, Math.round(Number(initialData?.lotSize) || 0)),
      mode: initialData?.inspectionMode,
      level: initialData?.aqlLevel,
      aqlMajor: initialData?.aqlMajor ?? undefined,
      aqlMinor: initialData?.aqlMinor ?? undefined,
      acMajor: initialData?.acMajor ?? undefined,
      reMajor: initialData?.reMajor ?? undefined,
      acMinor: initialData?.acMinor ?? null,
      reMinor: initialData?.reMinor ?? null,
    },
    { recomputeOnMount: !isEditMode || isLegacy || initialData?.reMajor === null || initialData?.reMajor === undefined },
  );

  const form = useForm({
    defaultValues: {
      productionOrderId: initialData?.productionOrderId || prefill?.productionOrderId || "",
      componentId: initialData?.componentId || "",
      routingStepId: initialData?.routingStepId || prefill?.routingStepId || "",
      stageProcessId: initialData?.stageProcessId || "",
      processStep: defaultProcessStep,
      processStage: defaultProcessStage,
      reportStage: isEditMode ? normalizeReportStage(initialData?.reportStage) : "SETUP",
      setupSampleQty: Number(initialData?.setupSampleQty ?? 5),
      setupDefectQty: Number(initialData?.setupDefectQty ?? 0),
      setupNotes: initialData?.setupNotes || "",
      sampleFrequency: frequencyLabel(initialData?.sampleFrequency) || "",
      machineId: initialData?.machineId || prefill?.machineId || "",
      operatorName: initialData?.operatorName || "",
      shift: initialData?.shift || "CA_1",
      productId: initialData?.productId || "",
      customerId: initialData?.customerId || "",
      templateId: initialData?.templateId || "",
      inspectorId: initialData?.inspectorId || currentUser?.id || "",
      referenceSampleNo: initialData?.referenceSampleNo || "",
      sampleRef: initialData?.sampleRef || "",
      sampleShiftLead: initialData?.sampleShiftLead || "",
      notes: initialData?.notes || "",
    },
    onSubmit: async ({ value }) => {
      const action = actionRef.current;
      actionRef.current = "save";
      const nextErrors: Record<string, string> = {};
      if (!value.productionOrderId) nextErrors.productionOrderId = "Chọn lệnh SX";
      if (!value.inspectorId) nextErrors.inspectorId = "Chọn người kiểm";
      if (!String(value.processStep || "").trim()) nextErrors.processStep = "Nhập công đoạn";

      const counts = summarizeQcDefects(defectDetails);
      const allCriteria = [...fourMItems, ...checklistItems];
      const verdict = computeQcVerdict({ counts, reMajor: sampling.reMajor, reMinor: sampling.reMinor, criteria: allCriteria });
      const closing = action === "close";
      const finalStatus = resolveFormStatus(verdict.verdict, override);
      if (closing && finalStatus === "WARNING" && !overrideReason.trim()) nextErrors.overrideReason = "Nhân nhượng phải ghi lý do";
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      const setupResult = isLegacy ? legacySetupResult : setupFailed ? "FAILED" : ipqcSetupResultFrom4M(fourMItems);
      let reportStage = normalizeReportStage(value.reportStage);
      if (closing) reportStage = "FINAL_BATCH";
      else if (reportStage === "FINAL_BATCH") reportStage = rounds.length > 0 ? "PATROL_IN_PROGRESS" : "SETUP";
      const status = closing ? finalStatus : setupResult === "FAILED" ? "FAILED" : "INSPECTING";

      // Tổng chốt lô tự tính; phiếu cũ chưa có lần kiểm giữ số đã lưu
      const keepStoredTotals = isLegacy && rounds.length === 0;
      const totals = rollupIpqcLot(value.setupSampleQty, value.setupDefectQty, rounds);
      const qtySample = keepStoredTotals ? Number(initialData?.qtySample ?? initialData?.sampleSize ?? 5) || 5 : Math.max(1, totals.totalSampled);
      const qtyDefect = keepStoredTotals ? Number(initialData?.qtyDefect ?? 0) : totals.totalNg;
      const keepStoredCounts = isLegacy && defectDetails.length === 0;

      await onSubmit({
        productionOrderId: value.productionOrderId,
        componentId: value.componentId || null,
        routingStepId: value.routingStepId || null,
        stageProcessId: value.stageProcessId || null,
        processStep: value.processStep,
        processStage: value.processStage,
        reportStage,
        setupResult,
        setupSampleQty: Math.max(0, Number(value.setupSampleQty) || 0),
        setupDefectQty: Math.max(0, Number(value.setupDefectQty) || 0),
        setupNotes: value.setupNotes || null,
        sampleFrequency: value.sampleFrequency || null,
        patrolLogs: rounds,
        machineId: value.machineId || null,
        operatorName: value.operatorName || null,
        shift: value.shift || null,
        productId: value.productId || null,
        customerId: value.customerId || null,
        templateId: value.templateId || null,
        inspectorId: value.inspectorId,
        lotSize: sampling.lotSize || null,
        aqlLevel: sampling.level,
        inspectionMode: sampling.mode,
        aqlMajor: sampling.aqlMajor,
        aqlMinor: sampling.aqlMinor,
        acMajor: sampling.acMajor,
        reMajor: sampling.reMajor,
        acMinor: sampling.acMinor,
        reMinor: sampling.reMinor,
        qtySample,
        qtyDefect,
        qtyPassed: Math.max(0, qtySample - qtyDefect),
        criticalDefects: keepStoredCounts ? Number(initialData?.criticalDefects) || 0 : counts.critical,
        majorDefects: keepStoredCounts ? Number(initialData?.majorDefects) || 0 : counts.major,
        minorDefects: keepStoredCounts ? Number(initialData?.minorDefects) || 0 : counts.minor,
        checklist: checklistItems,
        ...(isLegacy ? {} : { fourMChecklist: fourMItems }),
        defectDetails,
        attachments,
        notes: value.notes || null,
        status,
        overrideReason: closing && status === "WARNING" ? overrideReason.trim() : null,
        referenceSampleNo: value.referenceSampleNo || null,
        sampleRef: value.sampleRef || null,
        sampleShiftLead: value.sampleShiftLead || null,
        ...(action === "signoff" ? { sampleApprovedAt: true } : {}),
        ...(action === "release" ? { releasedAt: true } : {}),
      });
    },
  });

  const selectedPoId = useStore(form.store, (st: any) => st.values.productionOrderId);
  const processStage = useStore(form.store, (st: any) => st.values.processStage);
  const templateId = useStore(form.store, (st: any) => st.values.templateId);
  const reportStage = useStore(form.store, (st: any) => st.values.reportStage);
  const setupSampleQty = useStore(form.store, (st: any) => st.values.setupSampleQty);
  const setupDefectQty = useStore(form.store, (st: any) => st.values.setupDefectQty);
  const sampleFrequency = useStore(form.store, (st: any) => st.values.sampleFrequency);

  const { data: poDetails, isLoading: isLoadingPo } = useQuery({
    queryKey: ["po-for-qc-process", selectedPoId],
    queryFn: async () => {
      if (!selectedPoId) return null;
      try {
        return await getProductionOrderByIdQuery({ id: selectedPoId });
      } catch {
        return null;
      }
    },
    enabled: !!selectedPoId,
  });

  useEffect(() => {
    if (!poDetails) return;
    if (poDetails.qtyPlan && !sampling.lotSize) sampling.setLotSize(Number(poDetails.qtyPlan));
    if (poDetails.productId || poDetails.product?.id) form.setFieldValue("productId", poDetails.productId || poDetails.product?.id);
    const cust = poDetails.customerId || poDetails.customer?.id || poDetails.salesOrder?.customerId;
    if (cust) form.setFieldValue("customerId", cust);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poDetails]);

  const availableRoutingSteps = useMemo(() => {
    if (!poDetails) return [];
    const direct = poDetails.routingSteps || [];
    if (direct.length > 0) return direct;
    return (poDetails.components || []).flatMap((c: any) => c.routingSteps || []);
  }, [poDetails]);
  const availableComponents = useMemo(() => poDetails?.components || [], [poDetails]);

  const { data: sysConfigs } = useQuery({
    queryKey: ["configs", "QC_OFFSET_STAGES"],
    queryFn: () => trpcClient.config.getAll.query(),
    staleTime: 5 * 60 * 1000,
  });
  // Công đoạn: danh mục Cấu hình > Chất lượng (dùng chung với mẫu kiểm tra QC); giữ bước mặc định của 6 công đoạn gốc
  const qcClass = useQcClassification();
  const dynamicStages = useMemo(
    () =>
      qcClass.processStages.length
        ? qcClass.processStages.map((st) => ({ ...st, defaultStep: OFFSET_PROCESS_STAGES.find((o) => o.code === st.code)?.defaultStep || st.label }))
        : OFFSET_PROCESS_STAGES,
    [qcClass.processStages],
  );
  const frequencyOptions = useMemo(() => {
    const direct = (sysConfigs as any)?.QC_SAMPLE_FREQUENCIES;
    const list = Array.isArray(direct) && direct.length > 0 ? direct : SAMPLE_FREQUENCY_OPTIONS;
    const opts = list
      .map((o: any) => (typeof o === "string" ? { value: o, label: o } : { value: frequencyLabel(o?.value ?? o?.code ?? o?.label), label: frequencyLabel(o?.label ?? o?.value) }))
      .filter((o: any) => o.value);
    if (sampleFrequency && !opts.some((o: any) => o.value === sampleFrequency)) opts.unshift({ value: sampleFrequency, label: sampleFrequency });
    return opts;
  }, [sysConfigs, sampleFrequency]);

  const resetCriteria = () => {
    form.setFieldValue("templateId", "");
    setFourMItems([]);
    setChecklistItems([]);
  };

  const handleSelectRoutingStep = (stepId: string) => {
    form.setFieldValue("routingStepId", stepId);
    const step = availableRoutingSteps.find((s: any) => s.id === stepId);
    if (!step) return;
    if (step.name) {
      form.setFieldValue("processStep", step.name);
      form.setFieldValue("processStage", inferProcessStageClient(step.name));
      if (!isEditMode) resetCriteria();
    }
    if (step.machineId) form.setFieldValue("machineId", step.machineId);
    if (step.componentId) form.setFieldValue("componentId", step.componentId);
  };

  const handleSelectStage = (code: string) => {
    form.setFieldValue("processStage", code);
    const def = dynamicStages.find((s: any) => s.code === code);
    if (def && !form.getFieldValue("routingStepId")) form.setFieldValue("processStep", def.defaultStep);
    if (!isEditMode) resetCriteria();
  };

  // Mẫu kiểm tra theo công đoạn: phiếu mới nạp bản Đang áp dụng; tách 4M (duyệt setup) và sản phẩm (ký mẫu)
  const { templates, selectTemplate } = useQcTemplateCriteria({
    qcType: "IPQC",
    processStage,
    templateId: templateId || null,
    checklistItems: isLegacy ? checklistItems : [...fourMItems, ...checklistItems],
    onTemplateChange: (id, tpl) => {
      form.setFieldValue("templateId", id || "");
      if (tpl && !isEditMode) {
        if (tpl.setupSampleQty) form.setFieldValue("setupSampleQty", Number(tpl.setupSampleQty));
        if (tpl.patrolFrequency) form.setFieldValue("sampleFrequency", tpl.patrolFrequency);
        if (tpl.aqlMajor) sampling.setAqlMajor(Number(tpl.aqlMajor));
        if (tpl.aqlMinor) sampling.setAqlMinor(Number(tpl.aqlMinor));
      }
    },
    onChecklistChange: (items) => {
      if (isLegacy) {
        setChecklistItems(items);
        return;
      }
      const { fourM, product } = splitIpqcItems(items);
      setFourMItems(fourM);
      setChecklistItems(product);
    },
    normalizeOptions: { pendingByDefault: true },
    disabled: isLegacy || signed,
  });

  const counts = useMemo(() => summarizeQcDefects(defectDetails), [defectDetails]);
  const totals = useMemo(() => rollupIpqcLot(setupSampleQty, setupDefectQty, rounds), [setupSampleQty, setupDefectQty, rounds]);
  const computedSetup = ipqcSetupResultFrom4M(fourMItems);
  const setupResult = setupFailed ? "FAILED" : computedSetup;
  const productPending = checklistItems.some((c) => c.required !== false && (!c.result || c.result === "PENDING"));
  const productFailed = checklistItems.some((c) => c.result === "FAIL");
  const canSignOff = !isLegacy && !signed && setupResult === "PASSED" && !productPending && !productFailed;
  const canRelease = !isLegacy && signed && !released && setupResult === "PASSED";
  const canClose = isLegacy || released;
  const repeatColumns = useMemo(() => repeatColumnsOf(fourMItems, checklistItems), [fourMItems, checklistItems]);
  const legacyRows = isLegacy ? legacyParamRows(initialData?.technicalParams) : [];
  const legacyTotals = isLegacy && rounds.length === 0;
  const displayTotals = legacyTotals
    ? {
        totalSampled: Number(initialData?.qtySample ?? 0) || 0,
        totalNg: Number(initialData?.qtyDefect ?? 0) || 0,
        rounds: 0,
      }
    : totals;
  const displayCounts =
    isLegacy && defectDetails.length === 0
      ? { critical: Number(initialData?.criticalDefects) || 0, major: Number(initialData?.majorDefects) || 0, minor: Number(initialData?.minorDefects) || 0 }
      : counts;

  const submit = (action: Action) => {
    actionRef.current = action;
    form.handleSubmit();
  };

  const productInfo = poDetails?.product || initialData?.productionOrder?.product;
  const customerInfo = poDetails?.customer || poDetails?.salesOrder?.customer || initialData?.productionOrder?.customer;

  const pickDefectFromRound = (opt: any, qty: number) => {
    setDefectDetails((prev) => {
      if (prev.some((d) => d.defectCode === opt.code)) return prev;
      return [
        ...prev,
        { defectCode: opt.code, defectName: opt.name, processStage: processStage || "PRINTING", severity: opt.severity, defectQty: qty, isRepeated: qty >= 2, note: "" },
      ];
    });
  };

  return (
    <form
      id="qc-process-form"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        submit("close");
      }}
      className="mx-auto max-w-7xl space-y-4 pb-10"
    >
      {/* Giai đoạn */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-indigo-200 bg-indigo-50/40 px-3 py-2">
        <span className="text-xs font-bold uppercase tracking-wider text-indigo-950">Giai đoạn</span>
        <IpqcStageBar
          newFlow={!isLegacy}
          reportStage={reportStage}
          onReportStageChange={(st) => form.setFieldValue("reportStage", normalizeReportStage(st))}
          setupDone={setupResult === "PASSED"}
          signed={signed}
          released={released}
          roundCount={rounds.length}
        />
      </div>

      {/* Thông tin chung + lấy mẫu */}
      <section className="space-y-3.5 rounded-xl border border-slate-200 bg-white p-4 shadow-2xs">
        <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
          <Layers className="h-4 w-4 text-indigo-600" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">Thông tin chung</h3>
          {isLegacy && (
            <Badge variant="outline" className="border-slate-300 bg-slate-50 text-[10px] text-slate-600">
              Phiếu cũ
            </Badge>
          )}
        </div>
        <QcInfoGrid>
          <QcInfoField label="Lệnh SX" required span={2} error={errors.productionOrderId}>
            <form.Field
              name="productionOrderId"
              children={(field) => (
                <SharedCombobox
                  type="productionOrder"
                  value={field.state.value}
                  onChange={(val) => {
                    field.handleChange(val);
                    form.setFieldValue("routingStepId", "");
                  }}
                  placeholder="Chọn lệnh SX..."
                  disabled={released}
                />
              )}
            />
          </QcInfoField>
          <QcInfoField label="Công đoạn" htmlFor="ipqc-step" required error={errors.processStep}>
            {availableRoutingSteps.length > 0 ? (
              <form.Field
                name="routingStepId"
                children={(field) => (
                  <Select value={field.state.value || ""} onValueChange={handleSelectRoutingStep} disabled={released}>
                    <SelectTrigger id="ipqc-step" className="h-9 w-full min-w-0 text-xs">
                      <SelectValue placeholder="Chọn công đoạn..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableRoutingSteps.map((step: any) => (
                        <SelectItem key={step.id} value={step.id} className="text-xs">
                          {step.stepOrder || 1}. {step.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            ) : (
              <form.Field
                name="processStep"
                children={(field) => (
                  <Input
                    id="ipqc-step"
                    value={field.state.value || ""}
                    onChange={(e) => field.handleChange(e.target.value)}
                    className="h-9 text-xs"
                    placeholder="VD: In offset"
                  />
                )}
              />
            )}
          </QcInfoField>
          <QcInfoField label="Nhóm công đoạn" htmlFor="ipqc-stage-group">
            <Select value={processStage || ""} onValueChange={handleSelectStage} disabled={released}>
              <SelectTrigger id="ipqc-stage-group" className="h-9 w-full min-w-0 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {dynamicStages.map((st: any) => (
                  <SelectItem key={st.code} value={st.code} className="text-xs">
                    {st.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </QcInfoField>
          {availableComponents.length > 0 && (
            <QcInfoField label="Bộ phận SP" htmlFor="ipqc-component">
              <form.Field
                name="componentId"
                children={(field) => (
                  <Select value={field.state.value || ""} onValueChange={field.handleChange}>
                    <SelectTrigger id="ipqc-component" className="h-9 w-full min-w-0 text-xs">
                      <SelectValue placeholder="Toàn bộ lệnh" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableComponents.map((c: any) => (
                        <SelectItem key={c.id} value={c.id} className="text-xs">
                          {c.componentName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </QcInfoField>
          )}
          <QcInfoField label="Máy">
            <form.Field
              name="machineId"
              children={(field) => (
                <SharedCombobox type="machine" value={field.state.value || ""} onChange={(v) => field.handleChange(v)} placeholder="Chọn máy..." />
              )}
            />
          </QcInfoField>
          <QcInfoField label="Ca" htmlFor="ipqc-shift">
            <form.Field
              name="shift"
              children={(field) => (
                <Select value={field.state.value || "CA_1"} onValueChange={field.handleChange}>
                  <SelectTrigger id="ipqc-shift" className="h-9 w-full min-w-0 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CA_1">Ca 1 (06–14h)</SelectItem>
                    <SelectItem value="CA_2">Ca 2 (14–22h)</SelectItem>
                    <SelectItem value="CA_3">Ca 3 (22–06h)</SelectItem>
                    <SelectItem value="HC">Hành chính</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </QcInfoField>
          <QcInfoField label="Thợ đứng máy" htmlFor="ipqc-operator">
            <form.Field
              name="operatorName"
              children={(field) => (
                <Input id="ipqc-operator" value={field.state.value || ""} onChange={(e) => field.handleChange(e.target.value)} className="h-9 text-xs" />
              )}
            />
          </QcInfoField>
          <QcInfoField label="Người kiểm" required error={errors.inspectorId}>
            <form.Field
              name="inspectorId"
              children={(field) => (
                <SharedCombobox type="user" params={{ processGroup: 'QC' }} value={field.state.value} onChange={(v) => field.handleChange(v)} placeholder="Chọn người kiểm..." />
              )}
            />
          </QcInfoField>
          <QcInfoField label="Mẫu chuẩn" htmlFor="ipqc-ref-sample">
            <form.Field
              name="referenceSampleNo"
              children={(field) => (
                <Input
                  id="ipqc-ref-sample"
                  value={field.state.value || ""}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Số / ngày ký mẫu"
                  className="h-9 text-xs"
                />
              )}
            />
          </QcInfoField>
          <QcInfoField label="Sản phẩm" span={2}>
            <QcInfoValue>
              {isLoadingPo ? "Đang tải..." : productInfo ? `${productInfo.productCode ? `${productInfo.productCode} · ` : ""}${productInfo.productName || ""}` : poDetails?.productName}
            </QcInfoValue>
          </QcInfoField>
          <QcInfoField label="Khách hàng">
            <QcInfoValue>{customerInfo?.shortName || customerInfo?.name}</QcInfoValue>
          </QcInfoField>
          <QcInfoField label="Tần suất" htmlFor="ipqc-frequency">
            <form.Field
              name="sampleFrequency"
              children={(field) => (
                <Select value={field.state.value || ""} onValueChange={field.handleChange}>
                  <SelectTrigger id="ipqc-frequency" className="h-9 w-full min-w-0 text-xs">
                    <SelectValue placeholder="Chọn tần suất" />
                  </SelectTrigger>
                  <SelectContent>
                    {frequencyOptions.map((o: any) => (
                      <SelectItem key={o.value} value={o.value} className="text-xs">
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </QcInfoField>
        </QcInfoGrid>
        <div className="border-t border-slate-100 pt-3">
          <SamplingRow sampling={sampling} idPrefix="ipqc" />
        </div>
      </section>

      {isLegacy ? (
        <>
          <Section title="Duyệt setup" testId="ipqc-legacy-setup" tone="blue">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <div className="space-y-1">
                <Label htmlFor="ipqc-legacy-setup-result" className="text-xs font-semibold">
                  Kết quả
                </Label>
                <Select value={legacySetupResult} onValueChange={setLegacySetupResult}>
                  <SelectTrigger id="ipqc-legacy-setup-result" className="h-9 w-full text-xs font-bold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PASSED">Đạt</SelectItem>
                    <SelectItem value="ADJUST_REQUIRED">Chỉnh máy</SelectItem>
                    <SelectItem value="FAILED">Không đạt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <form.Field
                name="setupSampleQty"
                children={(field) => (
                  <div className="space-y-1">
                    <Label htmlFor="ipqc-setup-sample" className="text-xs font-semibold">
                      Số mẫu
                    </Label>
                    <Input id="ipqc-setup-sample" type="number" min={0} value={field.state.value} onChange={(e) => field.handleChange(Math.max(0, Number(e.target.value) || 0))} className="h-9 text-xs font-mono" />
                  </div>
                )}
              />
              <form.Field
                name="setupDefectQty"
                children={(field) => (
                  <div className="space-y-1">
                    <Label htmlFor="ipqc-setup-defect" className="text-xs font-semibold">
                      Mẫu lỗi
                    </Label>
                    <Input id="ipqc-setup-defect" type="number" min={0} value={field.state.value} onChange={(e) => field.handleChange(Math.max(0, Number(e.target.value) || 0))} className="h-9 text-xs font-mono" />
                  </div>
                )}
              />
              <form.Field
                name="setupNotes"
                children={(field) => (
                  <div className="space-y-1">
                    <Label htmlFor="ipqc-setup-notes" className="text-xs font-semibold">
                      Ghi chú
                    </Label>
                    <Input id="ipqc-setup-notes" value={field.state.value || ""} onChange={(e) => field.handleChange(e.target.value)} className="h-9 text-xs" />
                  </div>
                )}
              />
            </div>
          </Section>
          <CriteriaTable items={checklistItems} onChange={setChecklistItems} />
          <LegacyMeasurements rows={legacyRows} />
        </>
      ) : (
        <>
          {/* 1. Duyệt setup (4M) */}
          <section data-testid="ipqc-setup-4m" className="space-y-2">
            <CriteriaTable
              title="1. Duyệt setup (4M)"
              items={fourMItems}
              onChange={signed ? undefined : setFourMItems}
              readOnly={signed}
              toolbar={
                <div className="flex flex-wrap items-center gap-2">
                  <QcTemplateSelect templates={templates} value={templateId} onChange={selectTemplate} disabled={signed} />
                  <Badge
                    variant="outline"
                    data-testid="ipqc-setup-result"
                    data-result={setupResult || "PENDING"}
                    className={cn(
                      "text-[11px] font-bold",
                      setupResult === "PASSED" && "border-emerald-300 bg-emerald-50 text-emerald-700",
                      setupResult === "ADJUST_REQUIRED" && "border-amber-300 bg-amber-50 text-amber-800",
                      setupResult === "FAILED" && "border-red-300 bg-red-50 text-red-700",
                      !setupResult && "border-slate-200 text-slate-500",
                    )}
                  >
                    Setup: {setupResult ? SETUP_RESULT_LABEL[setupResult] : "Chưa duyệt"}
                  </Badge>
                  {!released && (
                    <label className="flex items-center gap-1.5 text-[11px] font-semibold text-red-700">
                      <input
                        type="checkbox"
                        checked={setupFailed}
                        onChange={(e) => setSetupFailed(e.target.checked)}
                        className="h-3.5 w-3.5 accent-red-600"
                      />
                      Không đạt, dừng máy
                    </label>
                  )}
                </div>
              }
            />
          </section>

          {/* 2. Ký mẫu */}
          <Section
            title="2. Ký mẫu"
            testId="ipqc-signoff"
            tone="violet"
            right={
              signed ? (
                <Badge variant="outline" data-testid="ipqc-signed" className="border-violet-300 bg-white text-[11px] font-semibold text-violet-800">
                  Đã ký {formatVnDateTime(initialData.sampleApprovedAt)}
                  {initialData?.sampleApprovedByName ? ` · ${initialData.sampleApprovedByName}` : ""}
                </Badge>
              ) : null
            }
          >
            <CriteriaTable
              title="Chỉ tiêu sản phẩm"
              items={checklistItems}
              onChange={signed ? undefined : setChecklistItems}
              readOnly={signed}
              emptyText="Mẫu kiểm tra chưa có chỉ tiêu sản phẩm."
            />
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              <form.Field
                name="setupSampleQty"
                children={(field) => (
                  <div className="space-y-1">
                    <Label htmlFor="ipqc-setup-sample" className="text-xs font-semibold">
                      Số mẫu ký
                    </Label>
                    <Input
                      id="ipqc-setup-sample"
                      type="number"
                      min={0}
                      readOnly={signed}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(Math.max(0, Number(e.target.value) || 0))}
                      className="h-9 text-xs font-mono font-bold"
                    />
                  </div>
                )}
              />
              <form.Field
                name="setupDefectQty"
                children={(field) => (
                  <div className="space-y-1">
                    <Label htmlFor="ipqc-setup-defect" className="text-xs font-semibold text-rose-700">
                      Mẫu lỗi
                    </Label>
                    <Input
                      id="ipqc-setup-defect"
                      type="number"
                      min={0}
                      readOnly={signed}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(Math.max(0, Number(e.target.value) || 0))}
                      className="h-9 text-xs font-mono font-bold"
                    />
                  </div>
                )}
              />
              <form.Field
                name="sampleRef"
                children={(field) => (
                  <div className="space-y-1">
                    <Label htmlFor="ipqc-sample-ref" className="text-xs font-semibold">
                      Số mẫu lưu
                    </Label>
                    <Input
                      id="ipqc-sample-ref"
                      readOnly={signed}
                      value={field.state.value || ""}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="VD: tờ số 5"
                      className="h-9 text-xs"
                    />
                  </div>
                )}
              />
              <form.Field
                name="sampleShiftLead"
                children={(field) => (
                  <div className="space-y-1">
                    <Label htmlFor="ipqc-shift-lead" className="text-xs font-semibold">
                      Trưởng ca
                    </Label>
                    <Input
                      id="ipqc-shift-lead"
                      readOnly={signed}
                      value={field.state.value || ""}
                      onChange={(e) => field.handleChange(e.target.value)}
                      className="h-9 text-xs"
                    />
                  </div>
                )}
              />
              <form.Field
                name="setupNotes"
                children={(field) => (
                  <div className="col-span-2 space-y-1 md:col-span-1">
                    <Label htmlFor="ipqc-setup-notes" className="text-xs font-semibold">
                      Ghi chú
                    </Label>
                    <Input id="ipqc-setup-notes" value={field.state.value || ""} onChange={(e) => field.handleChange(e.target.value)} className="h-9 text-xs" />
                  </div>
                )}
              />
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-100 pt-3">
              {!signed && (
                <span className="mr-auto text-[11px] text-slate-500">
                  {setupResult !== "PASSED"
                    ? "Duyệt setup phải Đạt trước khi ký mẫu."
                    : productFailed
                      ? "Còn chỉ tiêu sản phẩm không đạt."
                      : productPending
                        ? "Chấm hết chỉ tiêu sản phẩm để ký mẫu."
                        : "Mẫu đạt, có thể ký."}
                </span>
              )}
              {released ? (
                <Badge variant="outline" data-testid="ipqc-released" className="border-emerald-300 bg-emerald-50 text-[11px] font-bold text-emerald-800">
                  Đã cho sản xuất {formatVnDateTime(initialData.releasedAt)}
                  {initialData?.releasedByName ? ` · ${initialData.releasedByName}` : ""}
                </Badge>
              ) : (
                <>
                  {!signed && (
                    <Button
                      type="button"
                      size="sm"
                      disabled={!canSignOff || isSubmitting}
                      onClick={() => submit("signoff")}
                      className="h-9 bg-violet-600 text-xs font-bold text-white hover:bg-violet-700"
                    >
                      <PenLine className="mr-1.5 h-4 w-4" />
                      Ký mẫu
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    disabled={!canRelease || isSubmitting}
                    title={signed ? "" : "Cần ký mẫu trước"}
                    onClick={() => submit("release")}
                    className="h-9 bg-emerald-600 text-xs font-bold text-white hover:bg-emerald-700"
                  >
                    <Play className="mr-1.5 h-4 w-4" />
                    Cho sản xuất
                  </Button>
                </>
              )}
            </div>
          </Section>
        </>
      )}

      {/* 3. Kiểm tra lặp lại */}
      <Section title={isLegacy ? "Kiểm tra lặp lại" : "3. Kiểm tra lặp lại"} testId="ipqc-patrol" tone="amber" right={sampleFrequency ? <span className="text-[11px] text-slate-600">Tần suất: {sampleFrequency}</span> : null}>
        <IpqcRoundsTable
          rounds={rounds}
          onChange={setRounds}
          columns={isLegacy ? [] : repeatColumns}
          processStage={processStage}
          sampleFrequency={sampleFrequency}
          defaultSampleQty={Number(setupSampleQty) || 5}
          onDefectPicked={pickDefectFromRound}
          disabled={!isLegacy && !released}
          disabledText="Mở sau khi cho sản xuất."
          legacy={isLegacy}
        />
      </Section>

      {/* Lỗi phát hiện — nguồn duy nhất của số lỗi theo mức */}
      <StageDefectSelectorTable
        processStage={processStage || "PRINTING"}
        lotSize={sampling.lotSize}
        sampleSize={displayTotals.totalSampled}
        defectDetails={defectDetails}
        onChange={(items) => setDefectDetails(items)}
        hideSampleSummary
      />

      {/* 4. Chốt lô */}
      <VerdictBar
        idPrefix="ipqc"
        counts={displayCounts}
        sampleSize={displayTotals.totalSampled}
        reMajor={sampling.reMajor}
        reMinor={sampling.reMinor}
        criteria={[...fourMItems, ...checklistItems]}
        override={override}
        onOverrideChange={setOverride}
        overrideReason={overrideReason}
        onOverrideReasonChange={setOverrideReason}
        reasonError={errors.overrideReason}
      >
        <div className="grid grid-cols-2 gap-2 border-t border-slate-100 pt-3 md:grid-cols-4" data-testid="ipqc-lot-summary">
          {[
            ["Cỡ lô", sampling.lotSize, "ipqc-lot-size"],
            ["Tổng mẫu", displayTotals.totalSampled, "ipqc-lot-sampled"],
            ["Mẫu lỗi", displayTotals.totalNg, "ipqc-lot-ng"],
            ["Số lần kiểm", rounds.length, "ipqc-lot-rounds"],
          ].map(([label, value, id]) => (
            <div key={id as string} className="min-w-0 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5">
              <div className="text-[10px] font-semibold text-slate-500">{label}</div>
              <div className="font-mono text-sm font-bold text-slate-900" data-testid={id as string}>
                {Number(value || 0).toLocaleString("vi-VN")}
              </div>
            </div>
          ))}
        </div>
        <div className="text-[11px] text-slate-500">
          {legacyTotals
            ? "Số liệu đã lưu của phiếu cũ."
            : `Tổng mẫu = mẫu ký ${Number(setupSampleQty) || 0} + ${rounds.length} lần kiểm; Ac/Re tra theo cỡ lô.`}
        </div>
        <form.Field
          name="notes"
          children={(field) => (
            <div className="space-y-1">
              <Label htmlFor="ipqc-notes" className="text-xs font-semibold text-slate-700">
                Nhận xét
              </Label>
              <Textarea id="ipqc-notes" rows={2} value={field.state.value || ""} onChange={(e) => field.handleChange(e.target.value)} className="min-h-9 text-xs" />
            </div>
          )}
        />
      </VerdictBar>

      <details className="rounded-xl border border-slate-200 bg-white shadow-2xs">
        <summary className="cursor-pointer px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-700">
          Ảnh &amp; tệp ({Array.isArray(attachments) ? attachments.length : 0})
        </summary>
        <div className="px-2 pb-2">
          <QcCategorizedMediaSection mode="QC" value={attachments} onChange={setAttachments} />
        </div>
      </details>

      <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 bg-white/95 py-3">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting} className="h-9 px-4 text-xs">
            Hủy
          </Button>
        )}
        <form.Subscribe selector={(state) => [state.isSubmitting]}>
          {([submitting]) => (
            <>
              <Button
                type="button"
                variant="outline"
                disabled={submitting || isSubmitting}
                onClick={() => submit("save")}
                className="h-9 border-blue-300 bg-blue-50 px-4 text-xs font-bold text-blue-700 hover:bg-blue-100"
              >
                <Save className="mr-1.5 h-4 w-4" />
                Lưu
              </Button>
              <Button
                type="submit"
                disabled={!canClose || submitting || isSubmitting}
                title={canClose ? "" : "Cho sản xuất trước khi chốt lô"}
                className="h-9 bg-indigo-600 px-5 text-xs font-bold text-white shadow-sm hover:bg-indigo-700"
              >
                {submitting || isSubmitting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
                Chốt lô
              </Button>
            </>
          )}
        </form.Subscribe>
      </div>
      {!isLegacy && !released && (
        <p className="flex items-center gap-1.5 text-[11px] text-slate-500">
          <ClipboardCheck className="h-3.5 w-3.5" />
          Duyệt setup 4M → Ký mẫu → Cho sản xuất (công đoạn chuyển Đang chạy) → Kiểm tra lặp lại → Chốt lô.
        </p>
      )}
    </form>
  );
}
